import APIS from 'src/constants/ApiConfig';
import { getAccessToken } from '../redux/actions/authCreator';
import { EPayStatus } from 'src/constants/Order';
import { MddCommon } from '@macrocura/utils';


/** 默认心跳定时 */
const defaultHeartInterval = 50;

/**
 * @description websocket 状态枚举
 * @type {number}
 */
export enum WSState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

/** 
 * @description webscoket 关闭 Code枚举
 * @type { number }
 */
enum WSCloseCode {
  /** 正常关闭 */
  NORMAL_CLOSURE = 1000,
  /** 离开 */
  GOING_AWAY = 1001,
  /** 协议错误 */
  PROTOCOL_ERROR = 1002,
  /** 不支持的数据 */
  CANNOT_ACCEPT = 1003,
  /** 没有收到状态 */
  NO_STATUS_CODE = 1005,
  /** 异常关闭 */
  CLOSED_ABNORMALLY = 1006,
  /** 无效的帧有效载荷数据 */
  NOT_CONSISTENT = 1007,
  /** 违反政策 */
  VIOLATED_POLICY = 1008,
  /** 讯息太大 */
  TOO_BIG = 1009,
}

/** 
 * @description websocket消息枚举
 * @type { string }
 */
enum WSCloseMessage {
  TokenNotValid = 'token not valid',
}

/**
 * @description websocket 消息类型枚举
 * @type { string }
 */
export enum WSMessageType {
  初始状态 = 'message_init',
  绑定订单状态 = 'order_status_update_bind',
  绑定发送药品清单结果 = 'prescription_order_send_status_update',
  绑定待认证医生 = 'doctor_account_update_bind',
  获取待处理订单 = 'order_status_update',
  获取待审核医生 = 'doctor_account_approve_update',
  获取待配置医生 = 'doctor_account_configure_update',
  发送药品清单结果 = 'prescription_order_send_status_update',
  HEARTBEAT = 'partner',
  WEBHEARTBEAT = 'web_heartbeat',
  COMMONBIND = 'common_bind'
}

/** 
 * @description websocket 消息数据
 */
export interface WSMessage {
  type?: WSMessageType;
  status?: string;
  /** 心跳 partner */
  channelType?: string;
  order_status?: string;
  /** 本次状态改变中，订单当前状态 last --> current 有两种可能：10 -->  30  |  0 --> 30 */
  current_pay_status?: EPayStatus;
  /** 本次状态改变中，订单源状态 */
  last_pay_status?: EPayStatus;
  /** 是这个订单所属的配药中心id */
  from_drug_store_id?: number;
  /** 是这个通知即刻发出的时间(utc时间）， 注意这个通知是 在订单发生状态变化之后  才会发出通知的，  订单最后修改时间字段是： modified_time */
  notify_time?: string;
  /** 组名 */
  group_name?: string;
  /** 发送药品清单的uuid */
  order_id?: string;
}

/**
 * @description websocket 连接、接受消息、关闭、错误函数回调
 */
export interface WSCallBack {
  onOpenBack?: () => void;
  onMessageBack?: (data: WSMessage) => void;
  onCloseBack?: () => void;
  onErrorBack?: () => void;
}

const minInterval = 5;

const url = APIS.webSocketUrl(`/ws/common/`);

class WebSocketService {
  socket: WebSocket | undefined;
  sendMsgList: string[] = [];
  onOpenBack?: () => void;
  onMessageBack?: (data: WSMessage) => void;
  onCloseBack?: () => void;
  onErrorBack?: () => void;


  /** 心跳定时器 */
  private interval = defaultHeartInterval;
  /** 心跳定时事件 */
  private timer: any | undefined;

  private async connect(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      if (!!this.socket && !!this.isOpen) {
      } else {
        // 去连接
        const accessToken = getAccessToken();
        if (!!accessToken && (this.socket === undefined || this.socket.readyState === WSState.CLOSING || this.socket.readyState === WSState.CLOSED)) {
          this.socket = new WebSocket(`${url}?token=${accessToken}`);
        }
      }

      if (!!this.socket) {
        this.webSocketListener();
        await MddCommon.sleep(1000);
        resolve(true);
      } else {
        reject(false);
      }
    });
  }

  setCallBack(param: WSCallBack) {
    const { onOpenBack, onMessageBack, onCloseBack, onErrorBack } = param;
    if (!!onOpenBack) {
      this.onOpenBack = onOpenBack;
    }
    if (!!onMessageBack) {
      this.onMessageBack = onMessageBack;
    }
    if (!!onCloseBack) {
      this.onCloseBack = onCloseBack;
    }
    if (!!onErrorBack) {
      this.onErrorBack = onErrorBack;
    }
  }

  webSocketListener() {
    if (!!this.socket && this.socket.readyState !== WSState.CLOSING && this.socket.readyState !== WSState.CLOSED) {
      this.socket.onopen = (ev: Event) => {
        // console.log('onOpen', ev);
        if (!!this.onOpenBack) {
          // this.onOpenBack();
        }
        this.onPing(true);
      };
      this.socket.onmessage = (ev: MessageEvent) => {
        try {
          const data: WSMessage = JSON.parse(ev.data);
          // if (!!data.type && !!this.onMessageBack) {
          //   this.onMessageBack(data);
          // }
          if (!!data.type) {
            // if (!data.hasOwnProperty('status') && !!data.type) {
            if (!!this.onMessageBack) {
              this.onMessageBack(data);
            }
          } else {

          }
        } catch (error) {
          console.error(error);
        }
      };
      this.socket.onclose = (ev: CloseEvent) => {
        // console.log('onClose ', ev);
        //  服务器端断开连接返回：code=1003&&reason=token not valid不重连
        if (ev.reason !== WSCloseMessage.TokenNotValid) {
          // 忽略上一次socket断开事件
          if (ev.code !== WSCloseCode.CANNOT_ACCEPT && ev.code !== WSCloseCode.PROTOCOL_ERROR) {
            this.stopPing();
            if (this.socket === ev.currentTarget) {
              if (!!this.onCloseBack) {
                // this.onCloseBack();
              }
              this.reConnect();
            }
          }
        }
      };
      this.socket.onerror = (ev: ErrorEvent) => {
        // console.log('onError ', ev);
        // 忽略上一次socket断开事件

        if (this.socket === ev.currentTarget) {
          if (!!this.onErrorBack) {
            // this.onErrorBack();
          }
        }
        this.close();
      };
    }
  }

  /**
   * websocket 重新连接
   */
  private async reConnect() {
    if (!this.socket || !this.isOpen) {
      await this.connectSuccess();

      if (this.sendMsgList.length > 0 && !!this.socket && !!this.isOpen) {
        // 重连的时候触发
        for (const msg of this.sendMsgList) {
          this.socket.send(msg);
        }
      }
    }
  }

  /** 
   * @description websocket 连接成功/失败处理
   */
  async connectSuccess() {
    let isConnect = false;
    if (!!this.socket && !!this.isOpen) {
      isConnect = true;
    } else {
      isConnect = await this.connect();
    }

    if (!isConnect) {
      this.disConnect();
      this.stopPing();
      await MddCommon.sleep(1000 * minInterval);

      await this.connectSuccess();
    }
  }

  /**
   *  websocket 断开连接
   */
  disConnect(): void {
    if (!!this.socket && this.socket.readyState !== WSState.CLOSING && this.socket.readyState !== WSState.CLOSED) {
      this.socket.close();
      this.socket = undefined;
    }
  }

  /**
   * websocket 关闭连接
   */
  private close(): void {
    if (!!this.socket) {
      this.socket.close();
      this.socket = undefined;
    }
  }

  /**
   * websocket 是否连接
   */
  get isOpen(): boolean {
    return !!this.socket && this.socket.readyState === WSState.OPEN;
  }

  /**
   * websocket 发送消息
   * @param {WSMessage} data
   */
  async sendMessage(data: WSMessage) {
    await this.connectSuccess();

    if (this.socket && this.isOpen) {
      const tempMsgStr = JSON.stringify(data);
      if (this.sendMsgList.indexOf(tempMsgStr) < 0) {
        this.sendMsgList.push(tempMsgStr);
      }
      this.socket.send(tempMsgStr);
    }
  }

  /**
   * websocket 开启心跳
   * @param {boolean} wsOnline
   */
  onPing(wsOnline: boolean) {
    this.timer = setInterval(() => {
      if (wsOnline) {
        if (!!this.isOpen) {
          const data: WSMessage = {
            channelType: WSMessageType.HEARTBEAT,
            type: WSMessageType.WEBHEARTBEAT,
          };
          this.sendMessage(data);
        }
      } else {

      }
    }, this.interval * 1000);
  }

  /**
   * websocket 停止心跳行为
   */
  stopPing() {
    if (!!this.timer) {
      clearInterval(this.timer);
    }
  }

}
export default new WebSocketService();



// WEBPACK FOOTER //
// ./src/services/WebSocketService.ts